import { useEffect, useRef, useState } from "react";
import { ParentSize } from "@visx/responsive";
import { Group } from "@visx/group";
import { Pie } from "@visx/shape";
import type { PieArcDatum } from "d3-shape";
import { useTooltip, useTooltipInPortal, defaultStyles } from "@visx/tooltip";
import { ChartCard, Skeleton } from "@/components/reusable";
import { fetchClassSocietyDistribution } from "@/services/analyticsApi";
import { useAnalyticsFilters } from "@/hooks/useAnalyticsFilters";
import type { ClassSocietyDatum } from "@/types/analytics";

const DONUT_COLOURS = ["#185FA5", "#378ADD", "#85B7EB", "#1D9E75", "#5DCAA5", "#9FE1CB", "#888780"];

export function ClassSocietyDonut() {
  const filters = useAnalyticsFilters();
  const [data, setData] = useState<ClassSocietyDatum[] | null>(null);

  useEffect(() => {
    let active = true;
    setData(null);
    fetchClassSocietyDistribution(filters).then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, [filters]);

  return (
    <ChartCard title="Class society coverage" subtitle="% of fleet by vessel count" minHeight={320}>
      {!data ? (
        <Skeleton height="h-full" className="min-h-[180px]" />
      ) : data.length === 0 ? (
        <div className="py-10 text-center text-[12px] text-[var(--color-text-tertiary)]">
          No vessels in selection.
        </div>
      ) : (
        <div className="flex flex-col gap-3 h-full">
          <div className="flex-1 min-h-0 w-full">
            <ParentSize>
              {({ width, height }) =>
                width > 0 && height > 0 ? (
                  <DonutChart data={data} width={width} height={height} />
                ) : null
              }
            </ParentSize>
          </div>
          <ul className="flex flex-wrap gap-x-3 gap-y-1.5 justify-center">
            {data.map((d, i) => (
              <li
                key={d.society}
                className="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-secondary)]"
              >
                <span
                  className="w-2.5 h-2.5 rounded-[2px]"
                  style={{ background: DONUT_COLOURS[i % DONUT_COLOURS.length] }}
                  aria-hidden="true"
                />
                {d.society} {(d.percent * 100).toFixed(0)}%
              </li>
            ))}
          </ul>
        </div>
      )}
    </ChartCard>
  );
}

const tooltipStyles = {
  ...defaultStyles,
  background: "white",
  border: "1px solid rgba(0,0,0,0.14)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  fontSize: 12,
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: 8,
  padding: "8px 12px",
};

function DonutChart({
  data,
  width,
  height,
}: {
  data: ClassSocietyDatum[];
  width: number;
  height: number;
}) {
  const radius = Math.min(width, height) / 2;
  const centerX = width / 2;
  const centerY = height / 2;
  const inner = radius * 0.62;

  const { tooltipData, tooltipLeft, tooltipTop, tooltipOpen, showTooltip, hideTooltip } =
    useTooltip<ClassSocietyDatum>();
  const { containerRef, TooltipInPortal } = useTooltipInPortal({
    scroll: true,
    detectBounds: true,
  });

  return (
    <div ref={containerRef} className="relative w-full h-full">
      <svg width={width} height={height}>
        <Group top={centerY} left={centerX}>
          <Pie
            data={data}
            pieValue={(d) => d.percent}
            outerRadius={radius}
            innerRadius={inner}
            padAngle={0.008}
          >
            {(pie) =>
              pie.arcs.map((arc, i) => (
                <SweepingDonutArc
                  key={arc.data.society}
                  arcDatum={arc}
                  pathGenerator={pie.path}
                  fill={DONUT_COLOURS[i % DONUT_COLOURS.length] ?? DONUT_COLOURS[0]!}
                  delayMs={100 + i * 90}
                  onMouseMove={(e) => {
                    const svg = e.currentTarget.ownerSVGElement as SVGSVGElement | null;
                    const pt = svg?.getBoundingClientRect();
                    showTooltip({
                      tooltipData: arc.data,
                      tooltipLeft: pt ? e.clientX - pt.left : 0,
                      tooltipTop: pt ? e.clientY - pt.top : 0,
                    });
                  }}
                  onMouseLeave={hideTooltip}
                />
              ))
            }
          </Pie>
        </Group>
      </svg>
      {tooltipOpen && tooltipData && (
        <TooltipInPortal top={tooltipTop} left={tooltipLeft} style={tooltipStyles}>
          <div className="font-medium">{tooltipData.society}</div>
          <div className="text-[var(--color-text-secondary)]">
            {(tooltipData.percent * 100).toFixed(1)}% of fleet
          </div>
        </TooltipInPortal>
      )}
    </div>
  );
}

interface SweepingDonutArcProps {
  arcDatum: PieArcDatum<ClassSocietyDatum>;
  pathGenerator: (arcDatum: PieArcDatum<ClassSocietyDatum>) => string | null;
  fill: string;
  delayMs: number;
  durationMs?: number;
  onMouseMove: (e: React.MouseEvent<SVGPathElement>) => void;
  onMouseLeave: () => void;
}

/**
 * Donut wedge that sweeps open from `startAngle` to `endAngle` on mount.
 * On data update, tweens both angles from the currently-displayed pair to
 * the new pair so the wedge glides to its new position+size rather than
 * resetting to zero-length.
 *
 * Direct ref+state+RAF rather than framer's `animate={{d}}` because a
 * zero-length arc (startAngle === endAngle) generates a degenerate path
 * with different topology from a non-zero arc, which framer's string
 * interpolator can't morph cleanly.
 */
function SweepingDonutArc({
  arcDatum,
  pathGenerator,
  fill,
  delayMs,
  durationMs = 800,
  onMouseMove,
  onMouseLeave,
}: SweepingDonutArcProps) {
  // Mount: start at a zero-length wedge (both angles at arc.startAngle).
  const prevStartRef = useRef<number>(arcDatum.startAngle);
  const prevEndRef = useRef<number>(arcDatum.startAngle);
  const [d, setD] = useState<string>("");

  useEffect(() => {
    const fromStart = prevStartRef.current;
    const fromEnd = prevEndRef.current;
    const toStart = arcDatum.startAngle;
    const toEnd = arcDatum.endAngle;

    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      prevStartRef.current = toStart;
      prevEndRef.current = toEnd;
      setD(pathGenerator(arcDatum) ?? "");
      return;
    }

    let startTime: number | null = null;
    let rafId: number;
    const ease = (t: number) => 1 - Math.pow(1 - t, 3); // easeOutCubic

    const step = (now: number) => {
      if (startTime === null) startTime = now;
      const elapsed = now - startTime - delayMs;
      if (elapsed < 0) {
        rafId = requestAnimationFrame(step);
        return;
      }
      const t = Math.min(1, elapsed / durationMs);
      const eased = ease(t);
      const currentStart = fromStart + (toStart - fromStart) * eased;
      const currentEnd = fromEnd + (toEnd - fromEnd) * eased;
      prevStartRef.current = currentStart;
      prevEndRef.current = currentEnd;
      setD(
        pathGenerator({
          ...arcDatum,
          startAngle: currentStart,
          endAngle: currentEnd,
        }) ?? ""
      );
      if (t < 1) rafId = requestAnimationFrame(step);
    };

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
    // pathGenerator ref identity changes per parent render; closure captures
    // the current one at effect-run time, which is what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arcDatum.startAngle, arcDatum.endAngle, delayMs, durationMs]);

  return <path d={d} fill={fill} onMouseMove={onMouseMove} onMouseLeave={onMouseLeave} />;
}
